import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useStyling } from '../../contexts/StylingContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import { APP_VERSION } from '../../lib/appVersion';
import { mfaApi } from '../../services/api';
import { getApiErrorMessage } from '../../lib/apiError';
import { MfaCodeStep, MfaSetupStep, BackupCodesStep } from '../../components/auth/TwoFactorSteps';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mfa, setMfa] = useState(null);
  const [pendingSession, setPendingSession] = useState(null);
  const { login, completeLogin } = useAuth();
  const { styling, loading: stylingLoading } = useStyling();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await login(email, password);
      if (result?.mfa) setMfa(result.mfa);
      // Otherwise the redirect is handled by PublicRoute (restores the pre-logout page via
      // the `returnTo` key), which fires as soon as auth state flips.
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const backToPassword = (message) => {
    setMfa(null);
    setPassword('');
    setError(typeof message === 'string' ? message : '');
  };

  const verifyCode = async (code, remember) => {
    setError('');
    setIsLoading(true);
    try {
      const { data } = await mfaApi.verify(mfa.mfaToken, code, remember);
      if (data.backupCodes) setPendingSession(data);
      else completeLogin(data, email);
    } catch (err) {
      const message = getApiErrorMessage(err, 'Verification failed');
      if (err.response?.status === 401 || err.response?.status === 423) backToPassword(message);
      else setError(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const mfaStep = pendingSession ? (
    <BackupCodesStep codes={pendingSession.backupCodes} onDone={() => completeLogin(pendingSession, email)} />
  ) : mfa?.mfaSetupRequired ? (
    <MfaSetupStep loadSetup={() => mfaApi.setup(mfa.mfaToken).then((r) => r.data)} onSubmit={verifyCode} busy={isLoading} onCancel={backToPassword} />
  ) : mfa ? (
    <MfaCodeStep onSubmit={verifyCode} busy={isLoading} onCancel={backToPassword} />
  ) : null;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 grid-bg">
      <div className="w-full max-w-md">
        {/* Logo/Brand */}
        {!stylingLoading && styling?.loginTitle && (
          <div className="text-center mb-8">
            <h1 className="text-4xl font-black tracking-tight text-foreground mb-2">
              {styling.loginTitle}
            </h1>
            <p className="text-muted-foreground text-sm">MSP Management Platform</p>
          </div>
        )}

        {/* Login Card */}
        <div className="bg-card border border-border rounded-lg p-6 space-y-6">
          {!mfaStep && (
          <div>
            <h2 className="text-xl font-bold tracking-tight">Sign In</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Enter your credentials to access the platform
            </p>
          </div>
          )}

          {error && (
            <div
              className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive"
              data-testid="login-error"
            >
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {mfaStep || (
          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="username"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                data-testid="login-email-input"
                className="bg-background"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  data-testid="login-password-input"
                  className="bg-background pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  disabled={isLoading}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  title={showPassword ? 'Hide password' : 'Show password'}
                  data-testid="login-password-toggle"
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-black text-white hover:bg-black/90 btn-glow"
              disabled={isLoading}
              data-testid="login-submit-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground/60 mt-6" data-testid="login-version">
          Version {APP_VERSION}
        </p>
      </div>
    </div>
  );
}
